import { useEffect, useState } from 'react';

// A calendar icon with today's day-of-month number inside it (like the "Today"
// icon in Google Calendar). Drawn as an inline SVG so the number is real text.
//
// Shape: drawn in the same geometry as Tabler's calendar (TbCalendar) so it lines
// up with the react-icons calendars used next to it. Pass `size` for an exact
// width/height, or leave it at 1em to size with `font-size`. Colour comes from
// `currentColor`.
//
// The number refreshes at midnight, so a page left open overnight stays right.

const msUntilNextMidnight = (): number => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
  return Math.max(1000, next.getTime() - now.getTime());
};

interface Props {
  className?: string;
  /** Width/height — a number (px) or any CSS length. Defaults to 1em. */
  size?: number | string;
}

const TodayCalendarIcon: React.FC<Props> = ({ className, size = '1em' }) => {
  const [day, setDay] = useState(() => new Date().getDate());

  useEffect(() => {
    const timer = window.setTimeout(() => setDay(new Date().getDate()), msUntilNextMidnight());
    return () => window.clearTimeout(timer);
    // Re-arm after each change of day so it keeps updating every midnight.
  }, [day]);

  return (
    <svg
      className={className}
      width={size}
      height={size}
      style={{ width: size, height: size }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {/* Page, binder rings and header line: the exact geometry of Tabler's
          calendar (react-icons TbCalendar) so this icon and TbCalendar /
          TbCalendarMonth are the same shape and size at the same box size. */}
      <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" />
      <path d="M16 3v4" />
      <path d="M8 3v4" />
      <path d="M4 11h16" />
      {/* Today's date, centred in the body below the header line */}
      <text
        x="12"
        y="16.3"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="8.5"
        fontWeight="700"
        fontFamily="inherit"
        fill="currentColor"
        stroke="none"
      >
        {day}
      </text>
    </svg>
  );
};

export default TodayCalendarIcon;
